import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { LayoutDashboard, Settings, Info, Watch, UserPlus, X, Sun, Moon, LogOut, Link } from '@/utils/icons';
import { LucideIcon } from 'lucide-react';

/**
 * Interface for navigation option
 */
interface NavigationOption {
    label: string;
    path: string;
    icon: string | LucideIcon;
    onClick?: () => void;
}

/**
 * Interface for user information
 */
interface UserInfo {
    username: string;
    role?: string;
    full_name?: string;
    first_name?: string;
}

/**
 * Interface for NavigationMenu props
 */
interface NavigationMenuProps {
    /**
     * User information to determine navigation options
     */
    userInfo?: UserInfo | null;
    /**
     * Whether to use dark theme styling
     */
    isDark?: boolean;
    /**
     * Custom navigation options to override default
     */
    customNavigation?: NavigationOption[];
    /**
     * Custom navigation menu button
     */
    customNavButton?: React.ReactNode;
    /**
     * Whether to show the navigation menu
     */
    showNavigation?: boolean;
    className?: string;
}

/**
 * NavigationMenu component that provides a dropdown menu with navigation options.
 * Supports role-based navigation and custom navigation options.
 */
const NavigationMenu: React.FC<NavigationMenuProps> = ({
    userInfo,
    isDark = false,
    customNavigation,
    customNavButton,
    showNavigation = true,
    className = '',
}) => {
    const [menuOpen, setMenuOpen] = useState<boolean>(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { toggleTheme } = useTheme();

    /**
     * Handle user logout
     */
    const handleLogout = () => {
        localStorage.removeItem('access');
        localStorage.removeItem('refresh');
        localStorage.removeItem('user_info');
        navigate('/login');
    };

    /**
     * Check if a path is currently active
     */
    const isActive = (path: string) => {
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    /**
     * Handle navigation and close menu
     */
    const handleNavigation = (path: string) => {
        navigate(path);
        setMenuOpen(false);
    };

    /**
     * Get navigation options based on user role or custom navigation
     */
    const getNavigationOptions = (): NavigationOption[] => {
        if (customNavigation) {
            return customNavigation;
        }

        // Start with Dashboard (always shown for all users)
        const options: NavigationOption[] = [
            { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard }
        ];

        // Add Admin Dashboard for admins only
        if (userInfo?.role === 'admin') {
            options.push({ label: 'Admin Dashboard', path: '/admin-dashboard', icon: Settings });
        }

        // Add Team Info for coaches and admins
        if (userInfo?.role === 'coach' || userInfo?.role === 'admin') {
            options.push({ label: 'Team Information', path: '/team-information', icon: Info });
        }

        // Add Settings for coaches and admins
        if (userInfo?.role === 'coach' || userInfo?.role === 'admin') {
            options.push({ label: 'Settings', path: '/settings', icon: Settings });
        }

        // Add remaining options for all users
        options.push(
            { label: 'Connect to Wearable', path: '/connect-wearable', icon: Watch },
            { label: 'Register New User', path: '/register', icon: UserPlus }
        );

        // Catapult Vector (Teams) - not in this release
        // if (userInfo?.role === 'coach' || userInfo?.role === 'admin') {
        //     options.push(
        //         { label: 'Connect Catapult Vector (Teams)', path: '/connect-terra-coach', icon: Link }
        //     );
        // }

        return options;
    };

    // If custom navigation button is provided, return it
    if (customNavButton) {
        return customNavButton;
    }

    // If navigation is disabled, return null
    if (!showNavigation) {
        return null;
    }

    // If user is not logged in (no access token), don't show the menu
    const token = localStorage.getItem('access');
    if (!token) {
        return null;
    }

    return (
        <>
            {/* Backdrop */}
            {menuOpen && (
                <div 
                    className="cgm-sidebar-backdrop" 
                    onClick={() => setMenuOpen(false)}
                />
            )}

            {/* Menu Button */}
            <div className={className}>
                <button
                    onClick={() => setMenuOpen(!menuOpen)}
                    className="cgm-nav-arrow cgm-nav-arrow-active"
                    aria-label="Navigation menu"
                >
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                </button>
            </div>

            {/* Sidebar */}
            <aside className={`cgm-sidebar ${!menuOpen ? 'cgm-sidebar-hidden' : ''}`}>
                <div className="cgm-sidebar-header">
                    <span className="cgm-text-primary font-semibold text-lg">Menu</span>
                    <button 
                        onClick={() => setMenuOpen(false)}
                        className="cgm-text-secondary hover:cgm-text-primary transition-colors"
                        aria-label="Close menu"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <nav className="cgm-sidebar-nav">
                    {getNavigationOptions().map((option) => (
                        <button
                            key={option.path}
                            onClick={() => {
                                if (option.onClick) {
                                    option.onClick();
                                } else {
                                    handleNavigation(option.path);
                                }
                            }}
                            className={`cgm-sidebar-item ${
                                isActive(option.path) ? 'cgm-sidebar-item-active' : ''
                            }`}
                        >
                            {typeof option.icon === 'string' ? (
                                <span className="w-5 h-5">{option.icon}</span>
                            ) : (
                                <option.icon className="w-5 h-5" />
                            )}
                            <span>{option.label}</span>
                        </button>
                    ))}
                </nav>

                <div className="cgm-sidebar-footer">
                    <button
                        onClick={toggleTheme}
                        className="cgm-sidebar-item w-full mb-2"
                    >
                        {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                        <span>{isDark ? 'Light Mode' : 'Dark Mode'}</span>
                    </button>

                    <button 
                        onClick={() => {
                            setMenuOpen(false);
                            handleLogout();
                        }}
                        className="cgm-sidebar-logout"
                    >
                        <LogOut className="w-5 h-5" />
                        <span>Logout</span>
                    </button>
                </div>
            </aside>
        </>
    );
};

export default NavigationMenu; 